import { clientCookies, setCookie } from '../cookies';
import {
  parseScaleoClickParams,
  sanitizeScaleoClickParams,
  SCALEO_REJECTED_EVENT,
  type ScaleoClickParams,
} from './scaleoClickParams';

/**
 * Session Attribution Tracking
 *
 * Tracks user attribution data for Reward Gateway lead submissions:
 * - Landing Page Source: The first page a user visits in a session
 * - Apply Click Source: The page where the user clicked "Apply"
 * - Scaleo click params: `clkid` / `clktype` captured at an affiliate landing
 *
 * Session expires after 30 minutes of inactivity (aligned with GA behavior).
 * Uses a single cookie with JSON data for simplicity.
 */

export type AttributionData = {
  landingPageSource: string | undefined;
  applyClickSource: string | undefined;
  lastActivity: number;
  /**
   * Present only when the session was entered from an affiliate placement.
   * Deliberately part of the session record rather than the `tracking` cookie
   * so it expires with the session — see `scaleoClickParams.ts` (CORE-4767).
   */
  scaleo?: ScaleoClickParams | null;
};

// Single cookie name for all attribution data
export const ATTRIBUTION_COOKIE_NAME = 'mhg_attribution_tracking';

/** Session timeout: 30 minutes of inactivity, aligned with GA. */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Tab-scoped claim on the stored Scaleo pair (CORE-4767).
 *
 * The cookie alone cannot tell "same visit" from "new visit in a fresh tab" —
 * it survives a tab being closed, which is exactly the reported bug. A marker in
 * `sessionStorage` closes that: it is copied into a tab opened from an in-page
 * link (so in-session navigation and new tabs keep their attribution) but is
 * absent in a tab opened cold, where the pair is then dropped.
 */
export const SCALEO_TAB_MARKER_KEY = 'mhg_scaleo_tab';

/** Identifies which capture a tab is claiming, so a stale marker cannot match. */
function scaleoToken(scaleo: ScaleoClickParams): string {
  return `${scaleo.clkid ?? ''}|${scaleo.clktype ?? ''}`;
}

/**
 * `sessionStorage` access throws outright in some privacy modes, so every use is
 * guarded. When it is unavailable the marker reads as absent and attribution is
 * dropped — the safe direction here, since over-attribution is what costs money.
 */
function readTabMarker(): string | null {
  try {
    return window.sessionStorage.getItem(SCALEO_TAB_MARKER_KEY);
  } catch {
    return null;
  }
}

function writeTabMarker(scaleo: ScaleoClickParams | null): void {
  try {
    if (scaleo) {
      window.sessionStorage.setItem(SCALEO_TAB_MARKER_KEY, scaleoToken(scaleo));
    } else {
      window.sessionStorage.removeItem(SCALEO_TAB_MARKER_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

export function parseAttributionCookie(
  cookieValue: string | undefined,
): AttributionData | null {
  if (!cookieValue) return null;
  try {
    return JSON.parse(decodeURIComponent(cookieValue)) as AttributionData;
  } catch {
    return null;
  }
}

/**
 * Read the Scaleo pair for the *current* session out of the raw cookie value.
 *
 * Safe to call on the server — it takes the cookie value and the reference time
 * rather than touching `window`. Returns `null` when the session has expired, so
 * an attribution left over from an earlier visit is never applied to a lead.
 */
export function readSessionScaleoParams(
  cookieValue: string | undefined,
  now: number = Date.now(),
): ScaleoClickParams | null {
  const data = parseAttributionCookie(cookieValue);
  if (!data) return null;
  if (now - data.lastActivity >= SESSION_TIMEOUT_MS) return null;
  return sanitizeScaleoClickParams(data.scaleo);
}

/**
 * `Window.dataLayer` is declared in each consumer's own `global.d.ts`, none of
 * which are in this package's `tsconfig` program (`include: src/**` only), so it
 * is typed locally — augmenting the global here would collide with the
 * consumers' declarations when they compile this source.
 */
type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

class SessionAttributionTracker {
  /**
   * Read the pair from the landing URL, reporting a refusal exactly once per
   * capture.
   *
   * The GA event is pushed straight to `dataLayer` rather than through this
   * package's `PushDataLayerFn`: that type is injected as a React *prop*, and
   * this tracker is a non-React singleton (it already writes `document.cookie`
   * directly). The prop route is not open to it either — the consumers mount
   * `SessionAttributionProvider` from server layouts, and a function prop cannot
   * cross the server/client boundary.
   */
  private getScaleoFromUrl(): ScaleoClickParams | null {
    if (typeof window === 'undefined') return null;
    const { params, rejection } = parseScaleoClickParams(
      window.location.search,
    );
    if (rejection) this.reportRejection(rejection);
    return params;
  }

  private reportRejection(reason: string): void {
    try {
      const target = window as DataLayerWindow;
      (target.dataLayer ??= []).push({
        event: SCALEO_REJECTED_EVENT,
        reason,
      });
    } catch {
      // Analytics must never break capture
    }
  }

  private getCurrentPagePath(): string {
    if (typeof window === 'undefined') return '';
    const { protocol, host, pathname } = window.location;
    return `${protocol}//${host}${pathname}`;
  }

  private getCookieData(): AttributionData | null {
    if (typeof window === 'undefined') return null;
    const value = clientCookies().get(ATTRIBUTION_COOKIE_NAME)?.value;
    return parseAttributionCookie(value);
  }

  private setCookieData(data: AttributionData): void {
    if (typeof window === 'undefined') return;
    try {
      setCookie(
        ATTRIBUTION_COOKIE_NAME,
        encodeURIComponent(JSON.stringify(data)),
      );
    } catch {
      // Ignore cookie errors
    }
  }

  /**
   * Decide which Scaleo pair this tab may use within a still-live session.
   *
   * A pair arriving in the URL is a fresh attributed landing: it replaces the
   * stored one wholesale and this tab claims it. Otherwise a stored pair only
   * survives if this tab captured it — a tab opened cold carries no marker, so
   * it starts organic and the pair is cleared from the cookie.
   *
   * Clearing is deliberate: the server reads the cookie and cannot tell which
   * tab submitted, so leaving the pair would let an organic entry point keep
   * claiming the affiliate conversion. If another attributed tab is still open
   * it loses attribution too — the conservative direction, since the cost of
   * this ticket is over-attribution, not under-attribution.
   */
  private resolveScaleoForTab(
    fromUrl: ScaleoClickParams | null,
    stored: ScaleoClickParams | null,
  ): ScaleoClickParams | null {
    if (fromUrl) {
      writeTabMarker(fromUrl);
      return fromUrl;
    }
    if (stored && readTabMarker() !== scaleoToken(stored)) {
      writeTabMarker(null);
      return null;
    }
    return stored;
  }

  updateLastActivity(): void {
    const data = this.getCookieData();
    if (data) {
      this.setCookieData({ ...data, lastActivity: Date.now() });
    }
  }

  /**
   * Initializes session tracking - records landing page if session is new/expired.
   * Call this once on app mount (e.g., in root layout).
   */
  initSession(): void {
    if (typeof window === 'undefined') return;

    try {
      const scaleoFromUrl = this.getScaleoFromUrl();
      const data = this.getCookieData();

      if (data == null || Date.now() - data.lastActivity >= SESSION_TIMEOUT_MS) {
        // Session expired or new session - set new landing page. Any Scaleo
        // attribution from the previous session is dropped here, so an organic
        // landing starts unattributed.
        writeTabMarker(scaleoFromUrl);
        this.setCookieData({
          landingPageSource: this.getCurrentPagePath(),
          applyClickSource: undefined,
          lastActivity: Date.now(),
          scaleo: scaleoFromUrl,
        });
        return;
      }

      this.setCookieData({
        ...data,
        lastActivity: Date.now(),
        scaleo: this.resolveScaleoForTab(
          scaleoFromUrl,
          sanitizeScaleoClickParams(data.scaleo),
        ),
      });
    } catch {
      // Ignore cookie errors (e.g., cookies disabled)
    }
  }

  /**
   * Records the apply click source - the page where user clicked "Apply".
   * Call this in Apply button onClick handlers.
   */
  recordApplyClick(pageUrl?: string): void {
    if (typeof window === 'undefined') return;

    try {
      const data = this.getCookieData();
      const url = pageUrl ?? this.getCurrentPagePath();

      if (data) {
        this.setCookieData({
          ...data,
          applyClickSource: url,
          lastActivity: Date.now(),
        });
      } else {
        // No existing session - create one
        const scaleoFromUrl = this.getScaleoFromUrl();
        writeTabMarker(scaleoFromUrl);
        this.setCookieData({
          landingPageSource: this.getCurrentPagePath(),
          applyClickSource: url,
          lastActivity: Date.now(),
          scaleo: scaleoFromUrl,
        });
      }
    } catch {
      // Ignore cookie errors
    }
  }
}

// Singleton instance
export const sessionAttributionTracker = new SessionAttributionTracker();
